import React, { FunctionComponent, useEffect } from 'react';
import Head from 'next/head';
import styles from '../styles/CategoryPage.module.scss';
import { CategoryModel } from '../services/content/category/models/CategoryModel';
import { MetaDataModel } from '../services/content/meta/models/MetaDataModel';
import { getNavbarCategories } from '../services/configurator/navbar/NavbarConfigurator';
import { getCategoryBySlug } from '../services/content/category/Category';
import {
    getArticlesByCategoryId,
    getLeadingContentOfListBySlug,
    removeRepeatingArticles,
} from '../services/content/article/Article';
import { categoryCache } from '../services/Cache';
import { setupCategoriesPageTargeting } from '../services/ads/Targeting';
import { NavigationMenuContext } from '../contexts/NavigationMenuContext';
import Layout from '../components/Layout/Layout';
import { LdJson } from '../components/LdJson/LdJson';
import { Pages } from '../constants/Enums';
import { CategoryMainPage } from '../components/CategoryPage/CategoryMainPage';
import { CategoryPageListing } from '../components/CategoryPage/CategoryPageListing';
import { CategoryTitleSection } from '../components/CategoryPage/components/CategoryTitleSection';
import { ArticlePartialModel } from '../services/content/article/models/ArticlePartialModel';

const FIRST_PAGE = '1';

type Props = {
    articles: ArticlePartialModel[];
    categories: CategoryModel[];
    currentCategory: CategoryModel;
    meta: MetaDataModel | null;
    leadingArticles: ArticlePartialModel[];
    page: string;
    slug: string;
};

export async function getServerSideProps(context: any) {
    context.res.setHeader('Cache-Control', 'public, s-maxage=14, stale-while-revalidate=59');
    const slug = context.query.category[0];
    const page = context.query.category[1] ? context.query.category[1] : '1';

    const cacheKey = `/category?slug=${slug}&page=${page}`;

    if (categoryCache.get(cacheKey)) {
        const props = categoryCache.get(cacheKey);
        if (props) {
            return {
                props: {
                    articles: props.articles,
                    categories: props.categories,
                    meta: props.meta,
                    currentCategory: props.currentCategory,
                    leadingArticles: props.leadingArticles,
                    page: page,
                    slug: slug,
                },
            };
        }
    }

    const categories = await getNavbarCategories();
    const currentCategory = await getCategoryBySlug(slug);
    // Check if currentCategory is null or undefined
    if (!currentCategory) {
        return { notFound: true };
    }

    const leadingArticles = await getLeadingContentOfListBySlug(slug, 3);
    const articleListing = await getArticlesByCategoryId(currentCategory?.id, '18', page);
    const filteredArticles = removeRepeatingArticles(articleListing ? articleListing.articles : [], leadingArticles);

    categoryCache.set(cacheKey, {
        articles: filteredArticles,
        categories: categories,
        meta: articleListing ? articleListing.meta : null,
        currentCategory: currentCategory,
        leadingArticles: leadingArticles,
    });

    return {
        props: {
            articles: filteredArticles,
            categories: categories,
            meta: articleListing ? articleListing.meta : null,
            currentCategory: currentCategory,
            leadingArticles: leadingArticles,
            page: page ? page : '1',
            slug: slug,
        },
    };
}

export const CategoryPage: FunctionComponent<Props> = ({
    articles,
    categories,
    currentCategory,
    leadingArticles,
    meta,
    page,
    slug,
}) => {
    useEffect(() => {
        setupCategoriesPageTargeting(currentCategory);
    }, [currentCategory]);

    const title = `${currentCategory?.title} - woman.bg`;

    return (
        <NavigationMenuContext.Provider value={categories}>
            <Layout>
                <Head>
                    <title>{title}</title>
                    <LdJson page={Pages.CATEGORY} category={currentCategory} />
                    <meta name='description' content={currentCategory.description ?? ''} />
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                         window['sprtlTargeting'] = {
                            section: ['category'],
                            category: ['${currentCategory?.id ?? ''}']
                        }`,
                        }}></script>
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('section', 'category') : '';
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('category', '${currentCategory.id}') : '';
                         `,
                        }}></script>
                    <link rel='canonical' href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/${slug}`} />
                </Head>
                {currentCategory.parentId !== '2023072110350052249' && (
                    <CategoryTitleSection className='mb-3 mt-3' title={currentCategory.title} />
                )}
                <main className={`container ${styles.container}`}>
                    {page === FIRST_PAGE ? (
                        <CategoryMainPage
                            articles={articles}
                            leadingArticles={leadingArticles}
                            currentCategory={currentCategory}
                            meta={meta}
                            page={page}
                            slug={slug}
                        />
                    ) : (
                        <CategoryPageListing articles={articles} meta={meta} page={page} slug={slug} />
                    )}
                </main>
            </Layout>
        </NavigationMenuContext.Provider>
    );
};

export default CategoryPage;
